import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Route, Sparkles, ArrowRight, ArrowLeft, Check, Clock, Calendar, Car } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiConfig';
import axios from 'axios';
import { detectSegment, SEGMENT_LABELS } from '../utils/vehicleSegments';
import { LocationPicker } from '../components/LocationPicker';

const POPULAR_CITIES = [
  'Hamburg', 'Berlin', 'München', 'Köln', 'Frankfurt', 'Stuttgart',
  'Düsseldorf', 'Hannover', 'Bremen', 'Leipzig', 'Dresden', 'Nürnberg',
  'Dortmund', 'Essen', 'Freiburg', 'Kiel', 'Lübeck', 'Rostock',
];

const DAYS = [
  { key: 0, short: 'Mo', label: 'Montag' },
  { key: 1, short: 'Di', label: 'Dienstag' },
  { key: 2, short: 'Mi', label: 'Mittwoch' },
  { key: 3, short: 'Do', label: 'Donnerstag' },
  { key: 4, short: 'Fr', label: 'Freitag' },
  { key: 5, short: 'Sa', label: 'Samstag' },
  { key: 6, short: 'So', label: 'Sonntag' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    home_city: '',
    frequent_from: '',
    frequent_to: '',
  });

  // Commuter schedule state
  const [commuteEnabled, setCommuteEnabled] = useState(false);
  const [schedule, setSchedule] = useState(
    DAYS.map(d => ({ day_of_week: d.key, active: d.key < 5, time_forward: '08:00', time_return: '17:00' }))
  );
  const [corridorKm, setCorridorKm] = useState(5);
  const [allowDetour, setAllowDetour] = useState(true);

  // Vehicle state (only for drivers)
  const isDriver = (user?.role === 'Fahrer' || user?.role === 'Beide');
  const [vehicle, setVehicle] = useState({
    make: '', model: '', color: '', license_plate: '', segment: '',
  });
  const [segments, setSegments] = useState([]);
  const [segmentOverride, setSegmentOverride] = useState(false);

  useEffect(() => {
    if (!isDriver) return;
    axios.get(`${API}/vehicle-segments`).then(res => {
      setSegments(res.data.segments || []);
    }).catch(() => {});
  }, [isDriver]);

  // Auto-detect KBA segment when make/model changes (unless user manually overrode)
  useEffect(() => {
    if (segmentOverride) return;
    const detected = detectSegment(vehicle.make, vehicle.model);
    if (detected && detected !== vehicle.segment) {
      setVehicle(v => ({ ...v, segment: detected }));
    }
  }, [vehicle.make, vehicle.model, segmentOverride]);

  const selectedSegment = segments.find(s => s.segment_key === vehicle.segment);

  const firstName = user?.firstName || '';
  const TOTAL_STEPS = isDriver ? 5 : 4;

  const updateDay = (dow, field, value) => {
    setSchedule(prev => prev.map(d => d.day_of_week === dow ? { ...d, [field]: value } : d));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Save profile + vehicle (vehicle only if driver with data)
      const payload = {
        city: data.home_city,
        frequent_route_from: data.frequent_from || null,
        frequent_route_to: data.frequent_to || null,
      };
      if (isDriver && vehicle.make && vehicle.model) {
        payload.vehicle = {
          make: vehicle.make,
          model: vehicle.model,
          color: vehicle.color || 'Schwarz',
          license_plate: vehicle.license_plate || null,
          segment: vehicle.segment || null,
        };
      }
      await axios.put(`${API}/auth/me`, payload);
      // Also push to /api/users/profile for vehicle upsert (handles preis_km derivation)
      if (isDriver && vehicle.make && vehicle.model) {
        try {
          await axios.put(`${API}/users/profile`, { vehicle: payload.vehicle });
        } catch (e) { /* non-blocking */ }
      }

      // Save commuter schedule if enabled
      if (commuteEnabled && data.frequent_from && data.frequent_to) {
        await axios.put(`${API}/commuter/schedule`, {
          from_city: data.frequent_from,
          to_city: data.frequent_to,
          corridor_km: corridorKm,
          allow_detour: allowDetour,
          schedule: schedule,
        });
      }
    } catch (e) {
      console.warn('Onboarding save failed:', e);
    }
    setSaving(false);
    // STRICT DOMAIN SEPARATION: After onboarding always go to /dashboard.
    // The Orb (/orb) is a separate, voice-first domain the user opts into explicitly.
    navigate('/dashboard');
  };

  const skip = () => {
    navigate('/dashboard');
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 12, fontSize: 15,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#f1f5f9', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div data-testid="onboarding-wizard" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      padding: 20,
    }}>
      <div style={{
        width: '100%', maxWidth: 520, borderRadius: 20, overflow: 'hidden',
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(20px)',
      }}>
        {/* Progress */}
        <div style={{ display: 'flex', gap: 4, padding: '16px 24px 0' }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(s => (
            <div key={s} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: s <= step ? '#00d4ff' : 'rgba(255,255,255,0.1)',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>

        <div style={{ padding: '28px 28px 24px' }}>
          {/* Step 1: Heimatstadt */}
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <StepHeader icon={<MapPin size={20} style={{ color: '#00d4ff' }} />}
                title={firstName ? `Hey ${firstName}!` : 'Willkommen!'}
                subtitle="Woher kommst du?" color="rgba(0,212,255,0.15)" />
              <input data-testid="onboarding-home-city" type="text"
                placeholder="Deine Stadt eingeben..." value={data.home_city}
                onChange={e => setData({ ...data, home_city: e.target.value })}
                style={{ ...inputStyle, marginBottom: 16 }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {POPULAR_CITIES.map(c => (
                  <button key={c} type="button" data-testid={`city-chip-${c}`}
                    onClick={() => setData({ ...data, home_city: c })}
                    style={{
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                      transition: 'all 0.15s',
                      background: data.home_city === c ? 'rgba(0,212,255,0.2)' : 'rgba(255,255,255,0.05)',
                      border: data.home_city === c ? '1px solid #00d4ff' : '1px solid rgba(255,255,255,0.08)',
                      color: data.home_city === c ? '#00d4ff' : '#94a3b8',
                    }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Route */}
          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <StepHeader icon={<Route size={20} style={{ color: '#22c55e' }} />}
                title="Deine Stammstrecke" subtitle="Fährst du regelmäßig? (Optional)"
                color="rgba(34,197,94,0.15)" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <LocationPicker
                  value={data.frequent_from}
                  onChange={(name) => setData({ ...data, frequent_from: name })}
                  placeholder="Von..."
                  color="#22c55e"
                  testId="onboarding-route-from"
                />
                <LocationPicker
                  value={data.frequent_to}
                  onChange={(name) => setData({ ...data, frequent_to: name })}
                  placeholder="Nach..."
                  color="#ef4444"
                  testId="onboarding-route-to"
                />
              </div>
            </div>
          )}

          {/* Step 3: Commuter Setup */}
          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <StepHeader icon={<Calendar size={20} style={{ color: '#f59e0b' }} />}
                title="Pendelstrecke" subtitle="Aktiviere deinen Pendler-Modus (Optional)"
                color="rgba(245,158,11,0.15)" />

              {/* Enable toggle */}
              <label data-testid="commute-toggle" style={{
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                padding: '12px 16px', borderRadius: 12, marginBottom: 16,
                background: commuteEnabled ? 'rgba(0,212,255,0.1)' : 'rgba(255,255,255,0.03)',
                border: commuteEnabled ? '1px solid rgba(0,212,255,0.3)' : '1px solid rgba(255,255,255,0.08)',
                transition: 'all 0.2s',
              }}>
                <input type="checkbox" checked={commuteEnabled}
                  onChange={e => setCommuteEnabled(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: '#00d4ff' }} />
                <div>
                  <div style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600 }}>Pendler-Modus aktivieren</div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>Automatische Match-Benachrichtigungen</div>
                </div>
              </label>

              {commuteEnabled && (
                <>
                  {/* Weekly Matrix */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                    {DAYS.map(day => {
                      const d = schedule.find(s => s.day_of_week === day.key);
                      return (
                        <div key={day.key} data-testid={`commute-day-${day.key}`} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', borderRadius: 10,
                          background: d?.active ? 'rgba(255,255,255,0.04)' : 'transparent',
                          border: '1px solid rgba(255,255,255,0.06)',
                          opacity: d?.active ? 1 : 0.5,
                        }}>
                          <input type="checkbox" checked={d?.active || false}
                            onChange={e => updateDay(day.key, 'active', e.target.checked)}
                            style={{ accentColor: '#00d4ff' }} />
                          <span style={{ width: 28, color: '#f1f5f9', fontSize: 13, fontWeight: 600 }}>{day.short}</span>
                          {d?.active && (
                            <>
                              <Clock size={12} style={{ color: '#22c55e', marginLeft: 4 }} />
                              <input type="time" value={d.time_forward}
                                onChange={e => updateDay(day.key, 'time_forward', e.target.value)}
                                data-testid={`time-fwd-${day.key}`}
                                style={{ ...timeInputStyle }} />
                              <Clock size={12} style={{ color: '#ef4444' }} />
                              <input type="time" value={d.time_return}
                                onChange={e => updateDay(day.key, 'time_return', e.target.value)}
                                data-testid={`time-ret-${day.key}`}
                                style={{ ...timeInputStyle }} />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Corridor slider */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>Max. Umweg/Korridor</span>
                      <span style={{ color: '#00d4ff', fontSize: 13, fontWeight: 600 }}>{corridorKm} km</span>
                    </div>
                    <input type="range" min={0.5} max={15} step={0.5} value={corridorKm}
                      onChange={e => setCorridorKm(+e.target.value)}
                      data-testid="corridor-slider"
                      style={{ width: '100%', accentColor: '#00d4ff' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#475569' }}>
                      <span>0,5 km</span><span>15 km</span>
                    </div>
                  </div>

                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    color: '#94a3b8', fontSize: 13, padding: '8px 0',
                  }}>
                    <input type="checkbox" checked={allowDetour}
                      onChange={e => setAllowDetour(e.target.checked)}
                      style={{ accentColor: '#00d4ff' }} />
                    <div>
                      <span>Teilstrecken erlauben</span>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        Erlaubt Mitfahrern, nur einen Teil deiner Route zu buchen (Drop-in/Drop-out).
                        So erreichst du mehr Matches!
                      </div>
                    </div>
                  </label>
                </>
              )}
            </div>
          )}

          {/* Step 4: Vehicle + KBA (drivers only) */}
          {step === 4 && isDriver && (
            <div data-testid="onboarding-step-vehicle">
              <StepHeader icon={<Car size={20} style={{ color: '#a78bfa' }} />}
                title="Dein Fahrzeug" subtitle="KBA-Klasse + Preis pro km"
                color="rgba(167,139,250,0.15)" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input data-testid="veh-make" type="text" placeholder="Marke (z.B. VW)"
                    value={vehicle.make}
                    onChange={e => setVehicle({ ...vehicle, make: e.target.value })}
                    style={{ ...inputStyle, flex: 1 }} />
                  <input data-testid="veh-model" type="text" placeholder="Modell (z.B. Golf)"
                    value={vehicle.model}
                    onChange={e => setVehicle({ ...vehicle, model: e.target.value })}
                    style={{ ...inputStyle, flex: 1 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input data-testid="veh-color" type="text" placeholder="Farbe"
                    value={vehicle.color}
                    onChange={e => setVehicle({ ...vehicle, color: e.target.value })}
                    style={{ ...inputStyle, flex: 1 }} />
                  <input data-testid="veh-plate" type="text" placeholder="Kennzeichen (z.B. HH-AB 1234)"
                    value={vehicle.license_plate}
                    onChange={e => setVehicle({ ...vehicle, license_plate: e.target.value.toUpperCase() })}
                    style={{ ...inputStyle, flex: 1 }} />
                </div>

                {/* KBA segment dropdown */}
                <div style={{
                  marginTop: 6, padding: 12, borderRadius: 12,
                  background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#e9d5ff', fontSize: 12, fontWeight: 600 }}>
                      KBA-Fahrzeugklasse
                    </span>
                    {vehicle.segment && !segmentOverride && vehicle.make && (
                      <span style={{ color: '#22c55e', fontSize: 11 }}>
                        Auto-erkannt
                      </span>
                    )}
                  </div>
                  <select
                    data-testid="veh-segment"
                    value={vehicle.segment || ''}
                    onChange={e => {
                      setVehicle({ ...vehicle, segment: e.target.value });
                      setSegmentOverride(true);
                    }}
                    style={{
                      ...inputStyle, marginBottom: 0, cursor: 'pointer',
                      background: 'rgba(255,255,255,0.08)',
                    }}
                  >
                    <option value="">– Klasse wählen –</option>
                    {segments.map(s => (
                      <option key={s.segment_key} value={s.segment_key}>
                        {s.segment_name} ({s.preis_km?.toFixed(3)} €/km)
                      </option>
                    ))}
                  </select>
                  {selectedSegment && (
                    <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#94a3b8' }}>Berechneter Preis:</span>
                      <span style={{ color: '#00d4ff', fontWeight: 700 }}>
                        {selectedSegment.preis_km?.toFixed(3)} €/km (kurz) ·
                        {' '}{selectedSegment.preis_km_long?.toFixed(3)} €/km (lang)
                      </span>
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                    Die Klasse wird automatisch nach Marke & Modell vorgeschlagen.
                    Du kannst sie jederzeit überschreiben.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step: Done (TOTAL_STEPS) */}
          {step === TOTAL_STEPS && (
            <div data-testid="onboarding-step-4" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,212,255,0.15)', border: '2px solid rgba(0,212,255,0.3)',
              }}>
                <Sparkles size={28} style={{ color: '#00d4ff' }} />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>
                {firstName ? `${firstName}, alles klar!` : 'Alles klar!'}
              </h2>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: '#94a3b8', lineHeight: 1.6 }}>
                {data.home_city && (<>Heimat: <strong style={{ color: '#f1f5f9' }}>{data.home_city}</strong><br /></>)}
                {data.frequent_from && data.frequent_to && (
                  <>Strecke: <strong style={{ color: '#f1f5f9' }}>{data.frequent_from} → {data.frequent_to}</strong><br /></>
                )}
                {commuteEnabled && (
                  <>Pendler: <strong style={{ color: '#00d4ff' }}>Aktiv ({schedule.filter(d => d.active).length} Tage)</strong><br /></>
                )}
                {isDriver && vehicle.make && vehicle.model && (
                  <>Fahrzeug: <strong style={{ color: '#a78bfa' }}>
                    {vehicle.make} {vehicle.model}
                    {vehicle.segment && ` · ${SEGMENT_LABELS[vehicle.segment] || vehicle.segment}`}
                    {selectedSegment?.preis_km && ` · ${selectedSegment.preis_km.toFixed(3)} €/km`}
                  </strong><br /></>
                )}
                Dein Orb kennt dich jetzt. Sag einfach "Hey" und los geht's!
              </p>
            </div>
          )}

          {/* Navigation */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 28, gap: 12,
          }}>
            {step > 1 ? (
              <button data-testid="onboarding-back" onClick={() => setStep(step - 1)}
                style={navBtnStyle}>
                <ArrowLeft size={16} /> Zurück
              </button>
            ) : (
              <button data-testid="onboarding-skip" onClick={skip}
                style={{ padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
                  background: 'transparent', border: 'none', color: '#64748b', fontSize: 13 }}>
                Überspringen
              </button>
            )}

            {step < TOTAL_STEPS ? (
              <button data-testid="onboarding-next" onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && !data.home_city) ||
                  (step === 4 && isDriver && (!vehicle.make || !vehicle.model))
                }
                style={{
                  ...navBtnStyle,
                  background: (
                    (step === 1 && !data.home_city) ||
                    (step === 4 && isDriver && (!vehicle.make || !vehicle.model))
                  ) ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #00d4ff, #0ea5e9)',
                  color: '#fff', fontWeight: 600,
                  opacity: (
                    (step === 1 && !data.home_city) ||
                    (step === 4 && isDriver && (!vehicle.make || !vehicle.model))
                  ) ? 0.4 : 1,
                }}>
                Weiter <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="onboarding-finish" onClick={save} disabled={saving}
                style={{
                  ...navBtnStyle,
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: '#fff', fontWeight: 600,
                }}>
                <Check size={16} /> {saving ? 'Speichern...' : "Los geht's!"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepHeader({ icon, title, subtitle, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 12, display: 'flex',
        alignItems: 'center', justifyContent: 'center', background: color,
      }}>{icon}</div>
      <div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>{title}</h2>
        <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>{subtitle}</p>
      </div>
    </div>
  );
}

const navBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#94a3b8', fontSize: 14, transition: 'all 0.2s',
};

const timeInputStyle = {
  padding: '4px 6px', borderRadius: 6, fontSize: 12, width: 75,
  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#f1f5f9', outline: 'none',
};
